/**
 * Troca automática do tema da leitura (fatia 2, `docs/systems/reading-theme-auto.md`).
 * Montado uma vez no `AppFrame`, ao lado do `ProLapseWatcher`; não desenha nada.
 *
 * Só age quando TUDO vale: conta real · PRO · tema da capa EQUIPADO · online.
 * A leitura atual sai do `UserBooksContext` (a assinatura que o app já mantém,
 * mesma ordem `updatedAt desc` do card de Ajustes): marcar "lendo"/"lido",
 * trocar a capa ou registrar progresso chegam pelo snapshot — zero leitura
 * extra. A decisão é `decideReadingThemeAuto` (pura).
 *
 * ⚠️ NUNCA ligar isto a `visibilitychange`/`appStateChange` (BUG-243: o
 * Android retoma a cada ~10 s por horas). Troca feita noutro aparelho chega
 * pelo próprio snapshot ou na próxima abertura.
 */
import { useEffect, useRef } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useOptionalToast } from "../../../contexts/ToastContext";
import { useOptionalUserBooksContext } from "../../../contexts/UserBooksContext";
import { extractCoverPalette } from "../../share-studio/utils/bookPalette";
import { proxifyImageUrl } from "../../../utils/imageUtils";
import { READING_THEME_ENGINE_VERSION } from "../../../config/readingTheme";
import { generateReadingTheme } from "../services/readingThemeService";
import { deriveReadingTheme, readingThemeCoverKey } from "../utils/readingThemeDerive";
import { changedStatusIds, decideReadingThemeAuto, type AutoBook } from "../utils/readingThemeAuto";
import { readingThemeAutoSession } from "../utils/readingThemeAutoSession";

const PALETTE_SIZE = 6;
const COVER_PROXY_SLOT = "reading-theme";

const session = readingThemeAutoSession;

export function ReadingThemeAutoWatcher() {
  const { user, profile, effectiveIsPro } = useAuth();
  const books = useOptionalUserBooksContext();
  // Opcional: a confirmação visível é o próprio app mudando de cor.
  const toast = useOptionalToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const allBooks = books?.allBooks;
  const booksLoading = books?.isLoading ?? true;
  const equipped = profile?.themePreference === "current_reading";
  const saved = profile?.currentReadingTheme ?? null;
  const pref = profile?.readingThemeModePref;
  const active = !!user && !user.isAnonymous && effectiveIsPro && equipped && !booksLoading && !!allBooks;

  useEffect(() => {
    if (!active || !allBooks || !user) return;
    const list = allBooks as AutoBook[];
    const statusChanged = changedStatusIds(session.prevStatuses, list);
    session.prevStatuses = new Map(list.map((b) => [b.id, b.status]));
    const isBoot = !session.booted;
    session.booted = true;
    if (session.inFlight || (typeof navigator !== "undefined" && navigator.onLine === false)) return;

    const decision = decideReadingThemeAuto({
      saved,
      books: list,
      isBoot,
      regensThisSession: session.regens,
      attempted: session.attempted,
      statusChanged,
      baselinePages: session.baseline,
    });
    if (decision.kind === "wait") {
      session.baseline.set(decision.bookId, decision.pages);
      return;
    }
    if (decision.kind !== "regen") return;

    const { book, key } = decision;
    session.attempted.add(key);
    session.inFlight = true;
    void (async () => {
      try {
        const cover = await extractCoverPalette(proxifyImageUrl(book.coverUrl, COVER_PROXY_SLOT), PALETTE_SIZE);
        // Capa ilegível ou tema recusado: o tema anterior fica. Sem CF.
        if (!cover) return;
        const derived = deriveReadingTheme(cover.palette, cover.meanLuminance, pref);
        if (derived.status !== "ok") return;
        session.regens += 1;
        await generateReadingTheme({
          userBookId: book.id,
          tokens: derived.tokens,
          mode: derived.mode,
          engineVersion: READING_THEME_ENGINE_VERSION,
          coverUrl: readingThemeCoverKey(book.coverUrl),
        });
        // O app muda de cor sozinho: sem o aviso, parece bug.
        toastRef.current?.showToast(book.title ? `Seu tema agora é de "${book.title}"` : "Seu tema agora é da sua leitura atual");
      } catch {
        // Rede, teto diário, livro que mudou de status no caminho: o tema
        // anterior fica e a próxima tentativa é o próximo gatilho.
      } finally {
        session.inFlight = false;
      }
    })();
  }, [active, allBooks, user, saved, pref]);

  return null;
}
